import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label, sentinel } from '@sentinel/ui';
import { isWeakPassword, mapApiError, mapPasswordViolations } from '@sentinel/auth-core';
import { passwordSchema, type PasswordInput } from '../features/invitation/schema';
import { useInvitationLookup } from '../features/invitation/use-lookup';
import { useAcceptInvitation } from '../features/invitation/use-accept';

export function InvitationAcceptPage() {
  const { t } = useTranslation('invitation');
  const { token: routeToken } = Route.useParams();
  // Capture once: replaceState below blows the param away from history,
  // but we still need it to look up the invitation and submit acceptance.
  const tokenRef = useRef(routeToken);
  const token = tokenRef.current;
  const lookup = useInvitationLookup(token);
  const accept = useAcceptInvitation();
  const [topError, setTopError] = useState<string | null>(null);

  // Strip the token from history so a Back button / refresh / shared
  // history sync cannot replay the credential.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history.replaceState) return;
    const cleanPath = window.location.pathname.replace(/\/[^/]+$/, '/_consumed');
    window.history.replaceState(null, '', cleanPath + window.location.search);
  }, []);

  const form = useForm<PasswordInput>({ resolver: zodResolver(passwordSchema) });

  if (lookup.isLoading) {
    return <AuthCard><p>{t('loading')}</p></AuthCard>;
  }

  if (lookup.error) {
    const status = lookup.error.status;
    const errCode = lookup.error.body?.error;
    if (status === 404) {
      return (
        <AuthCard title={t('invitation_not_found_title')} subtitle={t('invitation_not_found_message')}>
          <p style={{ textAlign: 'center' }}>
            <Link to="/login">{t('sign_in')}</Link>
          </p>
        </AuthCard>
      );
    }
    if (status === 410 && errCode === 'expired') {
      return (
        <AuthCard title={t('invitation_expired_title')} subtitle={t('invitation_expired_message')}>
          <p style={{ textAlign: 'center' }}>{t('contact_admin')}</p>
        </AuthCard>
      );
    }
    if (status === 410 && errCode === 'already_accepted') {
      return (
        <AuthCard title={t('invitation_already_accepted_title')} subtitle={t('invitation_already_accepted_message')}>
          <p style={{ textAlign: 'center' }}>
            <Link to="/login">{t('sign_in')}</Link>
          </p>
        </AuthCard>
      );
    }
    return (
      <AuthCard>
        <Alert type="error" role="alert">{t('generic_error')}</Alert>
      </AuthCard>
    );
  }

  if (!lookup.data) {
    return (
      <AuthCard>
        <Alert type="error" role="alert">{t('generic_error')}</Alert>
      </AuthCard>
    );
  }

  const { email, org_name, role } = lookup.data;

  async function submitWith(payload: { token: string; password?: string }) {
    setTopError(null);
    try {
      await accept.mutateAsync(payload);
      window.location.assign('/launch');
    } catch (e) {
      const err = e as { status?: number; body?: unknown };
      if (isWeakPassword(err.body)) {
        const items = mapPasswordViolations(err.body);
        const message = items
          .map((it) => t(it.i18nKey, { ns: 'common', ...it.params }))
          .join(' ');
        form.setError('password', { type: 'policy', message });
        setTopError(null);
        return;
      }
      // Never surface raw body.message — invitation flows accept
      // tenant-controlled organisation metadata that could be weaponised
      // for phishing if rendered verbatim. Branch off the stable code.
      const errKey = mapApiError(err.body);
      setTopError(t(errKey, { ns: 'common' }));
    }
  }

  async function onPasswordSubmit(values: PasswordInput) {
    await submitWith({ token, password: values.password });
  }

  const confirmErrorRaw = form.formState.errors.confirm_password?.message;
  const confirmErrorMsg =
    confirmErrorRaw === 'passwords_must_match' ? t('passwords_must_match') : confirmErrorRaw;

  // SECURITY: backend no longer exposes whether an account exists for the
  // invitee (account-enumeration oracle). The form is uniform for both
  // new-account and existing-account flows; the password is only used by
  // the backend if a new account is being created and otherwise ignored.
  const title = t('new_user_title');
  const subtitle = t('new_user_subtitle', { org: org_name, role });
  const submitLabel = t('new_user_submit');

  return (
    <AuthCard title={title} subtitle={subtitle}>
      {topError ? (
        <Alert type="error" role="alert">{topError}</Alert>
      ) : null}

        <FieldRow>
          <Label htmlFor="email">{t('email_label')}</Label>
          <Input id="email" type="email" value={email} readOnly disabled />
        </FieldRow>

        <FieldRow>
          <Label htmlFor="role">{t('role_label')}</Label>
          <Input id="role" type="text" value={role} readOnly disabled />
        </FieldRow>

        <form
          onSubmit={form.handleSubmit(onPasswordSubmit)}
          style={{ display: 'grid', gap: '0.75rem' }}
        >
          <FieldRow>
            <Label htmlFor="password">{t('password_label')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.password ? 'true' : undefined}
              {...form.register('password')}
            />
            <FieldError message={form.formState.errors.password?.message} />
          </FieldRow>

          <FieldRow>
            <Label htmlFor="confirm_password">{t('confirm_password_label')}</Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.confirm_password ? 'true' : undefined}
              {...form.register('confirm_password')}
            />
            <FieldError message={confirmErrorMsg} />
          </FieldRow>

          <p style={{ color: sentinel.textMuted, fontSize: '0.875rem', margin: 0 }}>
            {t('password_only_for_new_accounts')}
          </p>

          <Button type="submit" disabled={accept.isPending}>
            {submitLabel}
          </Button>
        </form>
    </AuthCard>
  );
}

export const Route = createFileRoute('/invitation/$token')({
  component: InvitationAcceptPage,
});
